"use client"

import type { TrackEventProperties } from "@/modules/tracking/types"
import type { FunctionComponent, MouseEventHandler } from "react"

import { clsx } from "clsx"
import { useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Plus } from "@jobteaser/spark/components/Icons/Plus"

import { Link } from "@/modules/routing/components/Link"
import { useHref } from "@/modules/routing/hooks/useHref"
import { TrackButtonLink } from "@/modules/tracking/components/TrackButtonLink"

import styles from "./Tabs.module.css"

type TabLink = {
  label: string
  trackingProps?: TrackEventProperties
  url: string
}

type TabsProps = {
  id?: string
  links: TabLink[]
  numberOfItemsToShowWhenCollapsed?: number
  seeMoreLabel: string
}

export const Tabs: FunctionComponent<TabsProps> = ({
  id = "tabs",
  links,
  numberOfItemsToShowWhenCollapsed = 6,
  seeMoreLabel,
}) => {
  const href = useHref()
  const [expanded, setExpanded] = useState(false)

  const handleExpandClick: MouseEventHandler<HTMLButtonElement> = () => {
    setExpanded(true)
  }

  const handleItemClick: MouseEventHandler<HTMLAnchorElement> = () => {
    setExpanded(false)
  }

  let linksToDisplay: TabLink[] = links

  if (!expanded) {
    linksToDisplay = links.slice(0, numberOfItemsToShowWhenCollapsed)

    const isActiveLinkHidden = links.slice(numberOfItemsToShowWhenCollapsed).find(({ url }) => url === href)

    if (isActiveLinkHidden) {
      linksToDisplay.push(isActiveLinkHidden)
    }
  }

  return (
    <ul className={styles.tabs__list} id={id}>
      {linksToDisplay.map(({ label, trackingProps, url }) => (
        <li aria-current={href === url ? "page" : false} key={`${url}-${href === url}`}>
          {trackingProps ? (
            <TrackButtonLink
              className={clsx(styles.tabs__link, {
                [styles.tabs__link___active]: href === url,
              })}
              href={url}
              onClick={handleItemClick}
              size="small"
              trackingProps={trackingProps}
              variant="tertiary"
            >
              {label}
            </TrackButtonLink>
          ) : (
            <Button
              className={clsx(styles.tabs__link, {
                [styles.tabs__link___active]: href === url,
              })}
              href={url}
              onClick={handleItemClick}
              renderComponent={Link}
              size="small"
              variant="tertiary"
            >
              {label}
            </Button>
          )}
        </li>
      ))}

      {!expanded && links.length > numberOfItemsToShowWhenCollapsed && (
        <li>
          <Button
            aria-controls={id}
            aria-expanded={false}
            icon={Plus}
            onClick={handleExpandClick}
            size="small"
            variant="tertiary"
          >
            {seeMoreLabel}
          </Button>
        </li>
      )}
    </ul>
  )
}
